import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { CreateIntegracionRequest, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import {
    errorMessage,
    fetchIntegration,
    findParam,
    INTEGRATIONS_PATH,
    MASKED_VALUE,
    TODOIST_TOKEN_PARAM,
} from '../common/integration-common'

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

export interface TodoistStep {
    id: string
    text: string
    image?: string
}

const LOAD_ERROR = 'No se pudo consultar la integración con Todoist.'
const SAVE_ERROR = 'Error al guardar el token'

export const TODOIST_STEPS: TodoistStep[] = [
    { id: 'registro', text: 'Si aún no tienes cuenta, regístrate en Todoist.' },
    { id: 'usuario', text: 'En la pantalla principal da clic en el icono de tu usuario, en la esquina superior derecha.', image: '/integrations/todoist/todoist-step1.png' },
    { id: 'integraciones', text: 'Del menú que se despliega selecciona la opción Integraciones.', image: '/integrations/todoist/todoist-step2.png' },
    { id: 'token', text: 'En Integraciones abre la sección "Desarrolladores" y, en Token de API, da clic en Copiar al portapapeles.', image: '/integrations/todoist/todoist-step3.png' },
    { id: 'capturar', text: 'Pega el valor copiado en el campo de abajo y da clic en Guardar token.' },
]

const toRequest = (token: string): CreateIntegracionRequest => ({
    aplicacion: 'todoist',
    parametros: [{ parametro: TODOIST_TOKEN_PARAM, valor: token, sensible: true }],
})

export const useIntegrationTodoistModel = () => {
    const location = useLocation()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || INTEGRATIONS_PATH

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [connected, setConnected] = useState(false)
    const [token, setToken] = useState('')
    const [dirty, setDirty] = useState(false)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const items = await fetchIntegration('todoist')
                if (active) setConnected(Boolean(findParam(items, TODOIST_TOKEN_PARAM)?.valor))
            } catch (error) {
                console.error('Error al cargar el token', error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [])

    const changeToken = (value: string) => {
        setToken(value.replace(MASKED_VALUE, '').trim())
        setDirty(true)
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!dirty || !token) return
        try {
            setSaving(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokePOST('/integraciones', toRequest(token))).json()
            if (response.ok) {
                setConnected(true)
                setDirty(false)
                setToken('')
                setMessage({ type: 'success', message: response.message || 'Token guardado correctamente' })
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('Error al guardar el token', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    return {
        loading,
        saving,
        busy: loading || saving,
        busyLabel: loading ? 'Consultando integración…' : 'Guardando token…',
        message,
        connected,
        tokenValue: dirty ? token : connected ? MASKED_VALUE : '',
        canSave: dirty && Boolean(token),
        backTo: backUrl,
        changeToken,
        save,
    }
}
